// hypercomb-shared/ui/notes-viewer/notes-viewer.component.ts
//
// Centred modal that shows one note in full, with affordances for
// editing the text and adding another note to the same tile. All
// editing actions delegate back to the command line in capture mode —
// no in-place input here. That keeps every authoring path going through
// one place that knows about gold lighting and history commit.

import { Component, computed, signal, type OnDestroy } from '@angular/core'
import { EffectBus, type I18nProvider } from '@hypercomb/core'
import { TranslatePipe } from '../../core/i18n.pipe'

type Note = {
  id: string
  text: string
  children: Note[]
}

type NotesService = {
  notesFor(cellLabel: string): Note[]
  getNotes(cellLabel: string): Promise<Note[]>
}

@Component({
  selector: 'hc-notes-viewer',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './notes-viewer.component.html',
  styleUrls: ['./notes-viewer.component.scss'],
})
export class NotesViewerComponent implements OnDestroy {

  readonly visible = signal(false)
  readonly cellLabel = signal<string | null>(null)
  readonly noteId = signal<string | null>(null)
  readonly #version = signal(0)

  readonly note = computed<Note | null>(() => {
    this.#version()
    const cell = this.cellLabel()
    const id = this.noteId()
    if (!cell || !id) return null
    const svc = this.#notes
    return svc?.notesFor(cell).find(n => n.id === id) ?? null
  })

  #cleanups: (() => void)[] = []

  constructor() {
    this.#cleanups.push(EffectBus.on<{ cellLabel: string; noteId: string }>('notes:open', (p) => {
      if (!p?.cellLabel || !p?.noteId) return
      this.cellLabel.set(p.cellLabel)
      this.noteId.set(p.noteId)
      this.visible.set(true)
      // Announce visibility so the global escape cascade can close us
      // ahead of clearing selection. Without this, Escape falls through
      // to Priority 2 in escape-cascade.ts and the modal stays open.
      EffectBus.emit('notes:viewer', { active: true })
    }))

    // Cascade calls this when Escape lands while the viewer is the
    // top-most dismissable surface. Guarded on visible() so a stray
    // emit doesn't reset state when the viewer is already closed.
    this.#cleanups.push(EffectBus.on('notes:viewer-close', () => {
      if (this.visible()) this.close()
    }))

    this.#cleanups.push(EffectBus.on<{ segments?: readonly string[] }>('notes:changed', async (p) => {
      const cellLabel = Array.isArray(p?.segments) && p!.segments!.length > 0
        ? String(p!.segments![p!.segments!.length - 1] ?? '').trim()
        : ''
      const svc = this.#notes
      if (svc && cellLabel) await svc.getNotes(cellLabel)
      this.#version.update(v => v + 1)
      // close if the open note was deleted by an external write
      if (this.visible() && !this.note()) this.close()
    }))
  }

  ngOnDestroy(): void {
    for (const c of this.#cleanups) c()
  }

  close(): void {
    this.visible.set(false)
    this.cellLabel.set(null)
    this.noteId.set(null)
    EffectBus.emit('notes:viewer', { active: false })
  }

  /** Edit the open note — routes to command line in capture mode with a prefill. */
  edit(): void {
    const cell = this.cellLabel()
    const note = this.note()
    if (!cell || !note) return
    EffectBus.emit('note:capture', { cellLabel: cell, prefill: note.text, editId: note.id })
    this.close()
  }

  /** Add another note to the same tile. */
  addAnother(): void {
    const cell = this.cellLabel()
    if (!cell) return
    EffectBus.emit('note:capture', { cellLabel: cell })
    this.close()
  }

  /** Backdrop click → close. */
  onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close()
  }

  /** Esc → close. Bound on the modal. */
  onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  // ── service resolution ──────────────────────────────────

  get #notes(): NotesService | undefined {
    return get('@diamondcoreprocessor.com/NotesService') as NotesService | undefined
  }

  get i18n(): I18nProvider | undefined {
    return get('@hypercomb.social/I18n') as I18nProvider | undefined
  }
}
